import { data as commonProdData } from "@/profiles/production/common.prod"
import { data as commonPreviewData } from "@/profiles/preview/common.preview"

import { data as developmentProdData } from "@/profiles/production/development.prod"
import { data as corpProdData } from "@/profiles/production/corporate.prod"
// import { data as constProdData } from "@/profiles/production/construction.prod"
import { data as civilProdData } from "@/profiles/production/civil.prod"
import { data as sketchupProdData } from "@/profiles/production/sketchup.prod"
// import { data as geospatialProdData } from "@/profiles/production/geospatial.prod"
import { data as positioningProdData } from "@/profiles/production/positioning.prod"
import { data as e2eProdData } from "@/profiles/production/e2e.prod"
import { data as marketplaceProdData } from "@/profiles/production/marketplace.prod"
import { data as trimbleDeveloperProdData } from "@/profiles/production/trimble-developer.prod"

import { data as previewData } from "@/profiles/preview/preview"
// import { data as constructionPreviewData } from "@/profiles/preview/construction.preview"
import { data as corporatePreviewData } from "@/profiles/preview/corporate.preview"
// import { data as geospatialPreviewData } from "@/profiles/preview/geospatial.preview"
import { data as positioningPreviewData } from "@/profiles/preview/positioning.preview"
import { data as sketchupPreviewData } from "@/profiles/preview/sketchup.preview"
import { data as e2ePreviewData } from "@/profiles/preview/e2e.preview"
import { data as marketplacePreviewData } from "@/profiles/preview/marketplace.preview"
import { data as trimbleDeveloperPreviewData } from "@/profiles/preview/trimble-developer.preview"

import { data as ninetailedDevData } from "@/profiles/ninetailed.dev"

// You may want to define a type for the profile data if possible
export type ProfileData = Record<string, any>

const PROFILE_MAPPING: Record<string, ProfileData> = {
  "development-prod": developmentProdData,
  "corporate-prod": corpProdData,
  // "construction-prod": constProdData,
  "civil-prod": civilProdData,
  "sketchup-prod": sketchupProdData,
  // "geospatial-prod": geospatialProdData,
  "positioning-prod": positioningProdData,
  "e2e-prod": e2eProdData,
  "marketplace-prod": marketplaceProdData,
  "trimble-developer-prod": trimbleDeveloperProdData,
  preview: previewData,
  // "construction-preview": constructionPreviewData,
  "corporate-preview": corporatePreviewData,
  // "geospatial-preview": geospatialPreviewData,
  "positioning-preview": positioningPreviewData,
  "sketchup-preview": sketchupPreviewData,
  "ninetailed-dev": ninetailedDevData,
  "e2e-preview": e2ePreviewData,
  "marketplace-preview": marketplacePreviewData,
  "trimble-developer-preview": trimbleDeveloperPreviewData,
}

// Draft profiles (e.g. `sketchup-draft`, `corporate-draft`) are auto-derived
// from every `*-prod` profile and behave identically to their prod counterpart
// except that `NEXT_PUBLIC_DRAFT_MODE` is forced to `true`. This is handy for
// building a production-equivalent deploy that pulls draft Contentful content,
// enables Ninetailed preview, search debug, etc. (see Layer 4 below).
const DRAFT_PROFILES: string[] = Object.keys(PROFILE_MAPPING)
  .filter((name) => name.endsWith("-prod"))
  .map((name) => name.replace(/-prod$/, "-draft"))

export const VALID_PROFILES = [...Object.keys(PROFILE_MAPPING), ...DRAFT_PROFILES]

export const isValidProfile = (profile: string | undefined): profile is string =>
  typeof profile === "string" &&
  (profile in PROFILE_MAPPING || DRAFT_PROFILES.includes(profile))

export const getProfile = (profile: string | undefined): ProfileData => {
  // Fail fast when NEXT_PUBLIC_PROFILE is missing or doesn't match a known
  // profile. Silently falling back to a default mis-wires Contentful spaces,
  // Salesforce chat config, redirects, etc., and the misconfiguration only
  // surfaces at runtime in prod. Throwing here aborts `next build` with a
  // clear, actionable message.
  if (!isValidProfile(profile)) {
    const received =
      profile === undefined
        ? "(unset)"
        : profile === ""
          ? "(empty string)"
          : `'${profile}'`
    throw new Error(
      `[profiles] Invalid NEXT_PUBLIC_PROFILE: ${received}. ` +
        `Set NEXT_PUBLIC_PROFILE to one of: ${VALID_PROFILES.join(", ")}.`,
    )
  }

  // Resolve `*-draft` profiles to their `*-prod` base; record the draft flag
  // so it can be applied after the profile data is loaded but before env
  // overrides (env vars still win, matching the existing precedence model).
  const isDraft = profile.endsWith("-draft")
  const resolvedProfile = isDraft
    ? profile.replace(/-draft$/, "-prod")
    : profile

  const isPreview =
    resolvedProfile === "preview" || resolvedProfile.endsWith("-preview")

  const additionalEnvVariables = ["EXTRA_ENV_VAR", "NEXT_PUBLIC_DRAFT_MODE", "NEXT_PUBLIC_NINETAILED_PREVIEW_ENABLED"]

  // Layer 1: common defaults (common.prod.ts or common.preview.ts)
  // Layer 2: profile-specific overrides (.prod.ts / .preview.ts)
  // Layer 2b: `-draft` profile override — forces NEXT_PUBLIC_DRAFT_MODE = true
  // Layer 3: env variable overrides (ultimate override)
  const commonData = isPreview ? commonPreviewData : commonProdData
  const profileData: ProfileData = PROFILE_MAPPING[resolvedProfile]
  const merged: ProfileData = { ...commonData, ...profileData }
  if (isDraft) {
    merged.NEXT_PUBLIC_DRAFT_MODE = true
  }

  const allKeys = new Set([...Object.keys(merged), ...additionalEnvVariables])
  for (const key of allKeys) {
    const envValue = process.env[key]
    if (envValue !== undefined) {
      if (envValue === "false") {
        merged[key] = false
      } else if (envValue === "true") {
        merged[key] = true
      } else if (envValue !== "" && !isNaN(Number(envValue))) {
        merged[key] = Number(envValue)
      } else {
        merged[key] = envValue
      }
    }
  }

  // Layer 3.1: Contentful environment overrides must use literal
  // `process.env.NEXT_PUBLIC_*` reads — Next.js only inlines those at build
  // time. Dynamic `process.env[key]` above is not replaced, so SSR on Netlify
  // deploy previews (where branch-scoped vars are often build-only) would keep
  // reading profile defaults (`master`) even when the build log shows the
  // ticket env override.
  const mxpEnv = process.env.NEXT_PUBLIC_CF_MXP_ENVIRONMENT
  if (mxpEnv !== undefined && mxpEnv !== "") {
    merged.NEXT_PUBLIC_CF_MXP_ENVIRONMENT = mxpEnv
  }
  const rcEnv = process.env.NEXT_PUBLIC_CF_RC_ENVIRONMENT
  if (rcEnv !== undefined && rcEnv !== "") {
    merged.NEXT_PUBLIC_CF_RC_ENVIRONMENT = rcEnv
  }
  const datastorageEnv = process.env.NEXT_PUBLIC_CF_DATASTORAGE_ENV
  if (datastorageEnv !== undefined && datastorageEnv !== "") {
    merged.NEXT_PUBLIC_CF_DATASTORAGE_ENV = datastorageEnv
  }

  // Layer 3.5: SSG master switch — unless explicitly enabled, force every
  // `NEXT_PUBLIC_PRERENDER_*` flag off, regardless of its own value (whether
  // set in profile data or overridden via env var above). This guarantees a
  // single env var (`NEXT_PUBLIC_ENABLE_SSG`) controls whether any static
  // generation happens at all, defaulting to on-demand rendering (no SSG) so
  // build time and Netlify's prerendered-content blob storage stay bounded
  // as content volume grows.
  if (!merged.NEXT_PUBLIC_ENABLE_SSG) {
    for (const key of Object.keys(merged)) {
      if (key.startsWith("NEXT_PUBLIC_PRERENDER_")) {
        merged[key] = false
      }
    }
  }

  // Layer 4: draft-mode derivation — a single env var activates all draft behaviors
  if (merged.NEXT_PUBLIC_DRAFT_MODE) {
    merged.NEXT_PUBLIC_PREVIEW_DATA = true
    merged.NEXT_PUBLIC_NINETAILED_PREVIEW_ENABLED = true
    merged.NEXT_PUBLIC_SEARCH_DEBUG = true
    // OneTrust is disabled when the ID is blank.
    merged.NEXT_PUBLIC_ONETRUST_ID = ""
    // Blanking the OneTrust ID above skips the consent banner, but
    // ConsentProvider only runs the ninetailed.consent(true) + page()
    // handshake via the OneTrust accept flow or the BYPASS_CONSENT_CHECK
    // path — neither of which fires here. Ninetailed's privacy plugin
    // then falls back to its own default (deny for GDPR-region visitors,
    // implicit allow elsewhere), so personalization only appears to work
    // for non-EU testers on draft. See MXP-2826.
    merged.BYPASS_CONSENT_CHECK = true
  }

  return merged
}
